/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './PivotTable';

const PivotTableManager: ComponentManagerFn<'PivotTable'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'PivotTable'>(platform, 'PivotTable'),
    type: 'PivotTable',
    name: 'Pivot Table',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'PivotTableDateColumn',
          'PivotTableHiddenColumn',
          'PivotTableNumberColumn',
          'PivotTableStringColumn',
          'TriggerOnAggregateClick',
          'PivotTableCalculatedColumn',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      allowColumnResizing: {
        type: 'boolean',
        label: 'Allow Column Resizing',
        category: 'Pivot Table',
        helpText: 'Check this to allow users to resize grid columns.',
      },
      allowColumnSorting: {
        type: 'boolean',
        label: 'Allow Column Sorting',
        category: 'Pivot Table',
        helpText:
          'Check this to allow users to sort column values. This will enable click on header cell which will sort the values in ascending or descending order.',
      },
      hideColumnLines: {
        type: 'boolean',
        label: 'Hide Column Lines',
        category: 'Pivot Table',
        helpText: 'Check this to hide lines between the grid columns.',
      },
      showSingleLevelChildAtSameLevel: {
        type: 'boolean',
        label: 'Show Single Level Child At Same Level',
        category: 'Pivot Table',
        helpText:
          "Enable this to show the parent and it's child (only if it is single) at the same level.",
      },
      noDrilldown: {
        type: 'boolean',
        label: 'No Drilldown',
        category: 'Pivot Table',
        helpText: 'Enable this to hide drill down in the columns.',
      },
      hideConfigurator: {
        type: 'boolean',
        label: 'Hide Configurator',
        category: 'Pivot Table',
        helpText: 'Enable this to hide pivot configurator from the grid header',
      },
      maxFreezeRowsColumnWidth: {
        type: 'number',
        label: 'Max Freeze Rows Column Width',
        placeholder: 'Default 50% of grid width',
        helpText:
          'Enter a numeric value to control the maximum width of the frozen rows column.',
        step: 40,
        min: 200,
        max: 800,
      },
      groupByAttribute: {
        label: 'Group By Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        helpText:
          'Group by clause will be performed on the selected attribute.',
      },
      showGroupBefore: {
        type: 'boolean',
        label: 'Show Group Before',
        category: 'Pivot Table',
        helpText: 'Enable this to show the group summary first.',
      },
      emptyValueText: {
        type: 'string',
        label: 'Empty Value Text',
        category: 'Pivot Table',
        helpText: 'Enter the text to be displayed when the value is empty.',
      },
      aggregateColumnColors: {
        type: 'string',
        label: 'Aggregate Column Colors',
        category: 'Pivot Table',
        helpText:
          'Specify custom colors to be applied on aggregate column values. `darkgreen,green,#009900` will be used if left blank.',
      },
      rowColumnColors: {
        type: 'string',
        label: 'Row Column Colors',
        category: 'Pivot Table',
        helpText:
          'Specify custom colors to be applied on row column values e.g. red,blue,green',
      },
      summaryColumnColors: {
        type: 'string',
        label: 'Summary Column Colors',
        category: 'Pivot Table',
        helpText:
          'Specify custom colors to be applied on row summary column values e.g. red,blue,green',
      },
      hideRowSummary: {
        type: 'boolean',
        label: 'Hide Row Summary',
        category: 'Pivot Table',
        helpText: 'Enable this to hide row level summary values.',
      },
      hideFooter: {
        type: 'boolean',
        label: 'Hide Footer',
        category: 'Pivot Table',
        helpText: 'Enable this to hide the footer i.e. Grand Total.',
      },
      rowHeight: {
        type: 'number',
        label: 'Row Height',
        category: 'Pivot Table',
        helpText: 'Enter the height of the row in pixels.',
      },
      borders: {
        type: 'boolean',
        label: 'Borders',
        category: 'Pivot Table',
        helpText: 'Check this to add borders to the table',
      },
      configuration: {
        type: 'string',
        label: 'Default Configuration',
      },
    },
    isPropertyVisible: (property, itemData) => {
      if (property === 'configuration') {
        return false;
      }
      if (property === 'groupByAttribute') {
        return itemData.noDrilldown;
      }
      if (property === 'showGroupBefore') {
        return itemData.noDrilldown && !!itemData.groupByAttribute;
      }
      return true;
    },
    canHaveChildren: () => true,
    category: 'Pivot Table',
    icon: { iconName: 'table' },
    description: `**Overview**<br/>
The Pivot Table component is a powerful analytical tool that allows users to summarize, group, and explore large datasets interactively. Much like an Excel Pivot Table, it transforms flat data into a multidimensional grid, enabling users to calculate aggregations (sums, averages, counts) and view data from different perspectives.
Use this component for financial reporting, sales analysis, or any dashboard where users need to drill down into data hierarchies or cross-reference multiple data points.

**1. Data Structure & Columns**<br/>
The Pivot Table defines its data model through its child components. You must add specific column types to define what data is available for analysis.
* **Supported Columns:** PivotTableStringColumn, PivotTableNumberColumn, PivotTableDateColumn, PivotTableHiddenColumn, and PivotTableCalculatedColumn.
* **Grouping:** Unlike standard grids, these columns define the "Dimensions" (Rows/Columns) and "Measures" (Values) of your analysis.

**2. Layout & Interactivity**<br/>
You can heavily customize how the table behaves and how much control the end-user has.
* **Drilldown:** By default, the table supports hierarchical drill-down (expanding a row to see child records).
* **No Drilldown:** Enable this to flatten the view.
* **User Controls:**
    * **Hide Configurator:** Hides the drag-and-drop panel, preventing users from changing the layout at runtime.
    * **Allow Column Resizing / Sorting:** Gives users standard grid controls.
    * **Freeze Panes:** **Max Freeze Rows Column Width** controls how much of the "Row Header" section is locked in place while scrolling horizontally.

**3. Aggregation & Summaries**<br/>
The component automatically calculates totals, but you can refine how these are displayed.
* **Group By Attribute:** (Available when **No Drilldown** is active) Allows you to perform a specific SQL-like group by operation on a selected attribute.
* **Summary Visibility:**
    * **Hide Row Summary:** Removes the sub-total columns at the end of rows.
    * **Hide Footer:** Removes the "Grand Total" row at the bottom of the grid.
    * **Show Group Before:** Displays summary totals at the **start** of the group rather than the end.

**4. Visual Styling**<br/>
You can color-code specific sections of the matrix to highlight key data.
* **Colors:** Accepts comma-separated values (e.g., "red,blue,green") or hex codes.
* **Cell Styling:** Apply distinct colors to **Aggregate Columns**, **Row Columns**, and **Summary Columns**.
* **Grid Lines:** Options to **Hide Column Lines** or add **Borders** for high-contrast visibility.
* **Empty Value Text:** Defines what to display (e.g., "-") if a data cell has no value.

**5. Best Use Cases**<br/>
* **Sales Reports:** Grouping sales data by **Region** (Rows) and **Quarter** (Columns) to see total **Revenue** (Values).
* **Inventory Analysis:** Drilling down from **Category** > **Sub-Category** > **Product** to view stock counts.
* **Financial Statements:** Displaying hierarchical P&L (Profit and Loss) statements where rows expand to show detailed ledger entries.`,
  };
};

export default PivotTableManager;
